import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, WritableSignal, computed, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, of, timer } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { Globals } from '../globals';
import {
  AppUser,
  AppUserAuth,
  EncryptedUser,
} from '../interfaces/login.interfaces';
import { Language } from '../services/languages/languages.interfaces';
import { LogService } from '../services/log/log.service';
import { PagesConfig } from '../services/settings/app-init-settings.interfaces';
import { AppInitSettingsService } from '../services/settings/app-init-settings.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly globals = inject(Globals);
  private readonly appInitService = inject(AppInitSettingsService);
  private readonly logging = inject(LogService);

  /**
   * event for when a user logs out
   */
  logoutEvent: Subject<void> = new Subject();

  /**
   * event for when a user gets autologged in
   */
  autoLoginEvent: Subject<void> = new Subject();

  AUTH_ERROR = 401;
  securityObjectSignal: WritableSignal<AppUserAuth | undefined> =
    signal(undefined);
  isAuthenticated = computed(
    () => this.securityObjectSignal()?.isAuthenticated ?? false,
  );

  currentLanguage = computed(
    () => this.securityObjectSignal()?.language?.IdName ?? 'NL_BE',
  );

  currentLanguageId = computed(
    () => this.securityObjectSignal()?.language?.id ?? 1,
  );

  currentUserId = computed(() => this.securityObjectSignal()?.userId ?? 0);

  login(entity: AppUser): Observable<AppUserAuth> {
    const url = `${this.globals.apiRoot}/jwtsecurity/login`;
    return this.loginWithPath(entity, url);
  }

  loginEncrypted(entity: EncryptedUser): Observable<AppUserAuth> {
    const path = `${this.globals.apiRoot}/jwtsecurity/loginencrypted`;
    // Initialize security object
    this.resetSecurityObject();

    return this.http
      .post<AppUserAuth>(path, entity, this.globals.httpOptions)
      .pipe(
        tap((user: AppUserAuth) => {
          // Use object assign to update the current object

          this.securityObjectSignal.set(user);
          // Store into local storage
          localStorage.setItem(
            this.globals.localStorageTokenName,
            JSON.stringify(user),
          );
        }),
      );
  }

  loginAdminV2(token: { token: string }): Observable<AppUserAuth> {
    const url = `${this.globals.apiRoot}/jwtsecurity/adminloginv2`;

    // Initialize security object
    this.resetSecurityObject();

    return this.http.post<AppUserAuth>(url, token).pipe(
      tap((resp) => {
        this.securityObjectSignal.set(resp);
        // Store into local storage
        localStorage.setItem(
          this.globals.localStorageTokenName,
          JSON.stringify(resp),
        );
      }),
    );
  }

  loginMqtt(entity: AppUser): Observable<AppUserAuth> {
    this.logging.error('ASKING MQTT USER');
    const url = `${this.globals.apiRoot}/jwtsecurity/loginmqtt`;
    return this.loginWithPath(entity, url);
  }

  loginWithPath(entity: AppUser, path: string): Observable<AppUserAuth> {
    // Initialize security object
    this.resetSecurityObject();

    return this.http
      .post<AppUserAuth>(path, entity, this.globals.httpOptions)
      .pipe(
        tap((resp) => {
          this.securityObjectSignal.set(resp);
          // Store into local storage
          localStorage.setItem(
            this.globals.localStorageTokenName,
            JSON.stringify(resp),
          );
        }),
      );
  }

  loginAdmin(entity: AppUser): Observable<AppUserAuth> {
    // Initialize security object
    this.resetSecurityObject();

    const url = `${this.globals.apiRoot}/jwtsecurity/adminlogin`;
    return this.http
      .post<AppUserAuth>(url, entity, this.globals.httpOptions)
      .pipe(
        tap((resp) => {
          this.securityObjectSignal.set(resp);
          // Store into local storage
          localStorage.setItem(
            this.globals.localStorageTokenName,
            JSON.stringify(resp),
          );
        }),
      );
  }

  resaveToken(token: AppUserAuth): void {
    this.securityObjectSignal.set(token);

    // Store into local storage
    localStorage.setItem(
      this.globals.localStorageTokenName,
      JSON.stringify(token),
    );
  }

  saveAdminToken(token: AppUserAuth): void {
    try {
      this.resetSecurityObject();

      this.securityObjectSignal.set(token);
      // Store into local storage
      localStorage.setItem(
        this.globals.localStorageTokenName,
        JSON.stringify(token),
      );
    } catch (err) {
      console.error('error in saveAdiminToken', err);
    }
  }

  saveQrToken(token: AppUserAuth): void {
    try {
      this.resetSecurityObject();

      // Store into local storage
      localStorage.setItem(
        this.globals.localStorageTokenName,
        JSON.stringify(token),
      );
      this.securityObjectSignal.set(token);
    } catch (err) {
      console.error('error in saveQrToken', err);
    }
  }

  logout(redirectToLogin = true, returnUrl = 'dashboard'): void {
    // get returnUrl path from config
    const config: PagesConfig[] = (this.appInitService.configuration as any)[
      'pagesConfig'
    ];
    let returnConfig;
    if (config) {
      returnConfig = config
        .filter((x) => x.group === 'login')[0]
        .pages.filter((y) => y.type === 'login')[0].path;
    }

    // reset user data
    this.resetSecurityObject();

    // remove it from the storage
    localStorage.removeItem(this.globals.localStorageTokenName);

    if (redirectToLogin) {
      // send to login page
      this.router.navigate(['login'], {
        queryParams: {
          returnUrl: returnConfig === undefined ? returnUrl : returnConfig,
        },
      });
    }
  }

  refreshToken(): Observable<AppUserAuth | undefined> {
    const currentUser = this.getCurrentUserFromLocalStorage();

    if (currentUser) {
      const token = currentUser.refreshToken;
      const url = `${this.globals.apiRoot}/jwtsecurity/refresh`;

      // save mqtt details from previous data
      const mqttUsername = currentUser.mqttUsername;
      const mqttPassword = currentUser.mqttPassword;

      return this.http.post<AppUserAuth>(url, { token: token }).pipe(
        tap((resp) => {
          this.logging.debug('recieved refresh token', resp);

          resp.mqttPassword = mqttPassword;
          resp.mqttUsername = mqttUsername;

          this.securityObjectSignal.set(resp);

          // Store into local storage
          localStorage.setItem(
            this.globals.localStorageTokenName,
            JSON.stringify(resp),
          );
        }),
      );
    }
    return of(undefined);
  }

  retry = 0;

  getSavedUser(): void {
    const currentUser = this.getCurrentUserFromLocalStorage();

    if (currentUser !== null) {
      this.securityObjectSignal.set(currentUser);
      this.autoLoginEvent.next();
      this.refreshTokenWithRetries(4);
    }
  }

  refreshTokenWithRetries(tries = 2, delayBeforeRetryMs = 1000): void {
    this.refreshToken()
      .pipe(
        retry({
          count: tries,
          delay: (_error, retryIndex) => {
            const delay = Math.pow(2, retryIndex - 1) * delayBeforeRetryMs;
            return timer(delay);
          },
          resetOnSuccess: true,
        }),
        catchError((err: HttpErrorResponse) => {
          // only throw an error for the 401 unauth
          this.logging.error('refresh token error on get saved user', err);
          if (err.status === this.AUTH_ERROR) {
            throw err;
          } else {
            return of(undefined); // if not we do an undefined
          }
        }),
      )
      .subscribe({
        next: (appUserAuth: AppUserAuth | undefined) => {
          if (appUserAuth) {
            if (appUserAuth.isAuthenticated === true) {
              this.autoLoginEvent.next();
            }
          }
        },
        error: (error: Error) => {
          this.logging.error('refresh token error on get saved user', error);

          if (error instanceof HttpErrorResponse) {
            if (error.status === this.AUTH_ERROR) {
              this.logging.error(
                'refresh token error on get saved user',
                error,
              );
              this.logout(true);
            }
          }
        },
      });
  }

  getUserLanguage(): Language | null {
    if (!this.securityObjectSignal()) {
      this.getSavedUser();
    }

    const userAuth = this.securityObjectSignal();
    return userAuth?.language ?? null;
  }

  getUserLanguageId(): number {
    const language = this.getUserLanguage();

    if (!language) {
      const defaultLanguage = 3;
      this.logging.warn(
        `Language of user not found. Return default language ${defaultLanguage}.`,
      );
      return defaultLanguage;
    }

    return language.id;
  }

  getUserId(): number | undefined {
    if (!this.securityObjectSignal()) {
      this.getSavedUser();
    }

    const userAuth = this.securityObjectSignal();

    return userAuth?.userId;
  }

  getAuthToken(): string {
    const currentUser = this.getCurrentUserFromLocalStorage();
    // this.logger.debug('getAuthToken', localStorage.getItem(this.globals.localStorageTokenName));
    if (currentUser !== null) {
      return currentUser.accessToken;
    }

    return '';
  }

  /**
   * Reset the value in the security object
   */
  resetSecurityObject(): void {
    this.securityObjectSignal.set(new AppUserAuth());

    this.logoutEvent.next();
  }

  // This method can be called a couple of different ways
  // *hasClaim="'claimType'"  // Assumes claimValue is true
  // *hasClaim="'claimType:value'"  // Compares claimValue to value
  // *hasClaim="['claimType1','claimType2:value','claimType3']"
  hasClaim(claimType: string | string[], claimValue?: string): boolean {
    let ret = false;

    // See if an array of values was passed in.
    if (typeof claimType === 'string') {
      // this.logger.debug('claim is string');
      if (claimType === 'testClaim') {
        ret = true;
      } else {
        ret = this.isClaimValid(claimType, claimValue);
      }
    } else {
      // this.logger.debug('claim is array string');
      const claims: string[] = claimType;
      if (claims) {
        for (let index = 0; index < claims.length; index++) {
          ret = this.isClaimValid(claims[index]);
          // If one is successful, then let them in
          if (ret) {
            break;
          }
        }
      }
    }

    return ret;
  }

  private getCurrentUserFromLocalStorage(): AppUserAuth | null {
    const localStorageTokenName =
      localStorage.getItem(this.globals.localStorageTokenName) || null;
    return localStorageTokenName ? JSON.parse(localStorageTokenName) : null;
  }

  /**
   * Verify whether a claim is valid for the current user
   * @param claimType The name of the claim
   * @param claimValue The expected value of the claim
   */
  private isClaimValid(claimType: string, claimValue?: string): boolean {
    let auth: AppUserAuth | undefined = undefined;

    // Retrieve security object
    auth = this.securityObjectSignal();
    // this.logger.debug('auth=', auth);
    if (!auth) {
      return false;
    }
    // See if the claim type has a value
    // *hasClaim="'claimType:value'"
    if (claimType.indexOf(':') >= 0) {
      const words: string[] = claimType.split(':');
      claimType = words[0].toLowerCase();
      claimValue = words[1];
    } else {
      claimType = claimType.toLowerCase();
      // Either get the claim value, or assume 'true'
      claimValue = claimValue ? claimValue : 'true';
    }

    // Attempt to find the claim
    return (
      auth.claims.find(
        (c) =>
          c.claimType.toLowerCase() === claimType.toLowerCase() &&
          c.claimValue.toString().toLowerCase() === claimValue?.toLowerCase(),
      ) !== undefined
    );
  }
}
